import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { Badge } from "@opendate-app/setlist";
import "./styles/mySettingsStyles.css";

// Scroll-spy nav for the My Settings page. The section cards live in separate
// React roots (the UserSettings / EmailNotificationSettings bundles) and mount
// client-side, so we read their positions fresh on each scroll frame rather
// than wiring an observer at mount time.
const ACTIVE_OFFSET = 120; // px from viewport top a section must cross to be "active"
const MOBILE_QUERY = "(max-width: 767.98px)";

const SettingsSectionNav = ({ sections, sellerDetailsPath, label = "Settings sections" }) => {
  const [activeId, setActiveId] = useState(sections[0] ? sections[0].id : null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mql = window.matchMedia(MOBILE_QUERY);
    const apply = () => setIsMobile(mql.matches);
    apply();
    mql.addEventListener("change", apply);
    return () => mql.removeEventListener("change", apply);
  }, []);

  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (!sections.some((section) => section.id === id)) return undefined;

    let attempts = 0;
    const timer = window.setInterval(() => {
      const el = document.getElementById(id);
      attempts += 1;
      if (el) el.scrollIntoView({ block: "start" });
      if (el || attempts >= 50) window.clearInterval(timer);
    }, 100);
    return () => window.clearInterval(timer);
  }, [sections]);

  useEffect(() => {
    let frame = null;
    const compute = () => {
      frame = null;
      let current = sections[0] ? sections[0].id : null;
      for (const section of sections) {
        const el = document.getElementById(section.id);
        if (el && el.getBoundingClientRect().top <= ACTIVE_OFFSET) {
          current = section.id;
        }
      }
      setActiveId(current);
    };
    const onScroll = () => {
      if (frame === null) frame = window.requestAnimationFrame(compute);
    };
    compute();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, [sections]);

  const scrollTo = useCallback((id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  return (
    <nav className={`settings-nav${isMobile ? " settings-nav--chips" : ""}`} aria-label={label}>
      {sections.map((section) => (
        <button
          key={section.id}
          type="button"
          className={`settings-nav-link${activeId === section.id ? " is-active" : ""}`}
          aria-current={activeId === section.id ? "true" : undefined}
          onClick={scrollTo(section.id)}
        >
          {section.label}
          {section.count !== undefined ? (
            <span className="settings-nav-badge">
              <Badge color="gray" size="sm">
                {section.count}
              </Badge>
            </span>
          ) : null}
          {section.hasError ? <span className="settings-nav-error" role="img" aria-label="Needs attention" /> : null}
        </button>
      ))}
      {sellerDetailsPath ? (
        <a href={sellerDetailsPath} className="settings-nav-link">
          Seller Details
        </a>
      ) : null}
    </nav>
  );
};

SettingsSectionNav.propTypes = {
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      count: PropTypes.number,
      hasError: PropTypes.bool,
    })
  ).isRequired,
  sellerDetailsPath: PropTypes.string,
  label: PropTypes.string,
};

export default SettingsSectionNav;
